import { createSelector } from "@reduxjs/toolkit";
import * as CONST from "../../app/constants";
import { 
  fullName,
  humanReadableDate,
  IsDateInFuture,
  IsDateYearCurrentYear,
  IsTodayWithinRange,
  licenceSubTypeString
} from "../../app/utils";
import { 
  applicationTypeFromLicenceSubType,
  isCollectionOrDebtAgencyLicence,
  // applicationTypeFromLicenceSubType,
  // isMortgageTypeApplication,
  isRenewalTypeApplication,
  isRenewalTypeLicencingSubType 
} from "../slice/applicationsUtils";
import { AGENT_SORT_TYPES, STATUS_BY_OPTIONS } from "../slice/welcomeSlice";
import {mraPrincipalBrokerSelector} from "./mraPrincipalBrokerSelector";

const welcomePaneNote = (application, isRenewalEnable, renewalEndDate, enableStartNewApplication) => {
  let text = "";
  let mode = "";

  if(isRenewalTypeApplication(application) && 
        (application.applicationStatus === CONST.APPLICATION_STATUS_UNREAD || 
          application.applicationStatus === CONST.APPLICATION_STATUS_OPENED)) {
    mode = "blank"
    text = "Your renewal application was submitted and will be reviewed by Business Licensing. If more information about your application is needed, Business Licensing will contact you."
  } else if(isRenewalEnable) {
    mode = "blank"
    text = `Renewal applications are available until ${humanReadableDate(renewalEndDate)}. Before you start your renewal application, your business information must be up-to-date in the <a style="text-decoration: underline" href="https://rjsc.novascotia.ca/" target="_blank">Registry of Joint Stock Companies</a>.`
  } else if(enableStartNewApplication && application.licenceStatus === CONST.LICENCE_STATUS_EXPIRED) {
    mode = "blank"
    const appType = applicationTypeFromLicenceSubType(application.licencingSubType)

    if(appType === CONST.APPLICATION_TYPE_MORTGAGE_BROKERAGE_LICENCE) {
      text = `Your mortgage brokerage licence and individual mortgage broker licences expired on ${humanReadableDate(application.endDate)}. It is too late to renew your expired licences.  You must apply for a NEW mortgage brokerage licence and individual mortgage broker licences. Click on the "Start new application" button below.`
    } else if (appType === CONST.APPLICATION_TYPE_MORTGAGE_ADMINISTRATOR_LICENCE) {
      text = `Your mortgage administrator licence expired on ${humanReadableDate(application.endDate)}. It is too late to renew your expired licence.  You must apply for a NEW mortgage administrator licence. Click on the "Start new application" button below.`
    } else if (appType === CONST.APPLICATION_TYPE_MORTGAGE_LENDER_LICENCE) {
      text = `Your mortgage lender licence expired on ${humanReadableDate(application.endDate)}. It is too late to renew your expired licence. You must apply for NEW mortgage lender licence. Click on the "Start new application" button below.`
    } else {
      text = `Your collection or debt management agency/agent licences expired on ${humanReadableDate(application.endDate)}. It is too late to renew your expired licences, you must apply for NEW collection or debt management agency and agent licences. To apply for your NEW licences, click on the "Start new application" button below.`
    }   
  }

  return {
    text,
    mode,
  };
};

const getLicenceStatus = (agentApp) => {

  if (
    agentApp.licenceStatus === CONST.LICENCE_STATUS_NEW ||
    agentApp.applicationStatus === CONST.APPLICATION_STATUS_WITHDRAWN_BY_CLIENT ||
    agentApp.applicationStatus === CONST.APPLICATION_STATUS_WITHDRAWN_APPROVED || 
    agentApp.applicationStatus === CONST.APPLICATION_STATUS_DENIED
  ) {
    return agentApp.applicationStatus;
  }

  return agentApp.licenceStatus;
};

const agencyApplicationStatusString = (agencyApp) => {
  if (agencyApp.applicationStatus !== CONST.APPLICATION_STATUS_APPROVED_ISSUED) {
    switch (agencyApp.applicationStatus) {
      case CONST.APPLICATION_STATUS_PENDING:
        if(agencyApp?.businessLicenceData?.isDocumentUpdated) {
          return "Document review pending";
        }

        if (isRenewalTypeLicencingSubType(agencyApp.licencingSubType)) {
          return "Renewal Application Submitted";
        }
        return "Pending";
      case CONST.APPLICATION_STATUS_PENDING_AGENCY:
      case CONST.APPLICATION_STATUS_SUBMITTED:
      case CONST.APPLICATION_STATUS_UNREAD:
      case CONST.APPLICATION_STATUS_OPENED:
        if(agencyApp?.businessLicenceData?.isDocumentUpdated) {
          return "Document review pending";
        }
        return "Pending";
      case CONST.APPLICATION_RENEWAL_STATUS_SUBMITTED:
        return "Renewal Application Submitted";
      case CONST.APPLICATION_STATUS_CANCELLED: {
        if (agencyApp.licenceStatus === CONST.LICENCE_STATUS_WITHDRAWN_BY_CLIENT) {
          //When cancelled by client selected from adjudication screen
          return "Cancelled by client";
        }
        return "Cancelled";
      }

      case CONST.APPLICATION_STATUS_DENIED:
        return "Denied";
      case CONST.APPLICATION_STATUS_WITHDRAWN_BY_CLIENT:
        return "Cancelled by client";
      case CONST.APPLICATION_STATUS_WITHDRAWN_APPROVED:
        return "Cancelled";
      case CONST.APPLICATION_STATUS_APPROVED_ISSUED:
        return "Approved";
      default:
        // hack : to show "EXPIRED" for the archive applications of expired status
        if (
          agencyApp.originalApplicationId !== null &&
          agencyApp.applicationStatus === CONST.APPLICATION_STATUS_PENDING_PAYMENT
        ) {
          return "Expired";
        }
        return "";
    }
  } else {
    switch (agencyApp.licenceStatus) {
      case CONST.LICENCE_STATUS_ACTIVE:
        return "Active";
      case CONST.LICENCE_STATUS_EXPIRED:
        return "Expired";
      case CONST.LICENCE_STATUS_REVOKED:
        return "Revoked";
      case CONST.LICENCE_STATUS_SUSPENDED:
        return "Suspended";
      case CONST.LICENCE_STATUS_WITHDRAWN:
        return "Cancelled";
      case CONST.LICENCE_STATUS_WITHDRAWN_BY_CLIENT:
        return "Cancelled by client";
      default:
        return "";
    }
  }
};

// TODO : it is best to have a separate function for each licencing sub type
const agencyStatusDetails = (agencyApplication, principalBrokerApplication) => {
  const statusDetails = [];

  if (agencyApplication) {
    let statuStr =
      agencyApplication.licenceStatus === CONST.LICENCE_STATUS_NEW
        ? agencyApplication.applicationStatus
        : agencyApplication.licenceStatus;

    if (!statuStr) {
      statuStr = CONST.LICENCE_STATUS_NEW;
    }

    if (
      agencyApplication.licencingSubType ===
        CONST.LICENCE_SUB_TYPE_MORTGAGE_BROKERAGE_NEW_APPLICATION ||
      agencyApplication.licencingSubType ===
        CONST.LICENCE_SUB_TYPE_PRINCIPAL_BROKER_NEW_APPLICATION || 
      agencyApplication.licencingSubType ===
        CONST.LICENCE_SUB_TYPE_MORTGAGE_BROKERAGE_RENEWAL_APPLICATION
    ) {
      statusDetails.push({
        id: "licenceName",
        title: "Licence name",
        text: "Mortgage Brokerage Licence",
      });

      statusDetails.push({
        id: "licenceStatus",
        title: "Licence status",
        status: statuStr,
        text: agencyApplicationStatusString(agencyApplication),
      });

      statusDetails.push({
        id: "licenceNumber",
        title: "Licence number",
        text: agencyApplication.licenceNumber
          ? agencyApplication.licenceNumber
          : "N/A",
      });

      statusDetails.push({
        id: "endDate",
        title: "Licence expiry",
        text: agencyApplication.endDate
          ? humanReadableDate(agencyApplication.endDate)
          : "N/A",
      });

      statusDetails.push({
        id: "principalBroker",
        title: "Principal Broker",
        text: fullName(principalBrokerApplication?.businessLicenceData?.personData, "N/A"),
      });
    } else {
      if(agencyApplication.licencingSubType ===  CONST.LICENCE_SUB_TYPE_MORTGAGE_LENDER_NEW_APPLICATION 
        || agencyApplication.licencingSubType ===  CONST.LICENCE_SUB_TYPE_MORTGAGE_LENDER_RENEWAL_APPLICATION) {
            statusDetails.push({
              id: "licenceName",
              title: "Licence name",
              text: "Mortgage Lender Licence",
            });
      }

      if(agencyApplication.licencingSubType ===  CONST.LICENCE_SUB_TYPE_MORTGAGE_ADMINISTRATOR_NEW_APPLICATION
        || agencyApplication.licencingSubType ===  CONST.LICENCE_SUB_TYPE_MORTGAGE_ADMINISTRATOR_RENEWAL_APPLICATION) {
            statusDetails.push({
              id: "licenceName",
              title: "Licence name",
              text: "Mortgage Administrator Licence",
            });
      }

      statusDetails.push({
        id: "licenceStatus",
        title: "Licence status",
        status: statuStr,
        text: agencyApplicationStatusString(agencyApplication),
      });

      statusDetails.push({
        id : "licenceType",
        title : "Licence type",
        text : licenceSubTypeString(agencyApplication.licencingSubType)
      })

      statusDetails.push({
        id: "licenceNumber",
        title: "Licence number",
        text: agencyApplication.licenceNumber
          ? agencyApplication.licenceNumber
          : "N/A",
      });

      statusDetails.push({
        id: "endDate",
        title: "Licence expiry",
        text: agencyApplication.endDate
          ? humanReadableDate(agencyApplication.endDate)
          : "N/A",
      });

      if(agencyApplication.licencingSubType === CONST.LICENCE_SUB_TYPE_MORTGAGE_LENDER_NEW_APPLICATION || 
        agencyApplication.licencingSubType === CONST.LICENCE_SUB_TYPE_MORTGAGE_ADMINISTRATOR_NEW_APPLICATION || 
        agencyApplication.licencingSubType === CONST.LICENCE_SUB_TYPE_MORTGAGE_LENDER_RENEWAL_APPLICATION || 
        agencyApplication.licencingSubType === CONST.LICENCE_SUB_TYPE_MORTGAGE_ADMINISTRATOR_RENEWAL_APPLICATION) {
        statusDetails.push({
          id: "businessContact",
          title: "Compliance Officer",
          text: fullName(agencyApplication.businessLicenceData?.complianceOfficer, "N/A"),
        });
      } else if(agencyApplication.licencingSubType !== CONST.LICENCE_SUB_TYPE_INDIVIDUAL_PRINCIPAL_BROKER_APPLICATION
                && agencyApplication.licencingSubType !== CONST.LICENCE_SUB_TYPE_INDIVIDUAL_COMPLIANCE_OFFICER_APPLICATION) {
        statusDetails.push({
          id: "businessContact",
          title: "Business contact",
          text: agencyApplication.contactName ?? "",
        });

        if (
          agencyApplication.businessLicenceData &&
          agencyApplication.businessLicenceData.complianceOfficer
        ) {
          statusDetails.push({
            id: "complianceOfficer",
            title: "Business contact for compliance",
            text: fullName(
              agencyApplication.businessLicenceData?.complianceOfficer, "N/A"
            ),
          });
        } else {
          statusDetails.push({
            id: "complianceOfficer",
            title: "Business contact for compliance",
            text: "",
          });
        }
      }
    }
  }

  return statusDetails;
}

const agentApplicationStatusDetails = (agencyApplication,additionalApplications, components, renewalStartDate, renewalEndDate) => {
  
  if(!additionalApplications) {
    return []
  }

  const agentApplicationsStatus = [];

  let filteredAdditionalApplications = additionalApplications;

  if (
    components.welcome.agentSort === AGENT_SORT_TYPES.STATUS_VALUE &&
    components.welcome.agentSubFilter !== STATUS_BY_OPTIONS.ALL
  ) {
    filteredAdditionalApplications = additionalApplications.filter((app) => {
      // do not show any deleted application.
      if(app.markedDeleted) {
        return false;
      }

      switch (components.welcome.agentSubFilter) {
        case STATUS_BY_OPTIONS.DRAFT:
          return (
            !isRenewalTypeLicencingSubType(app.licencingSubType) && 
            app.licenceStatus === CONST.LICENCE_STATUS_NEW &&
            app.applicationStatus === CONST.APPLICATION_STATUS_PENDING_PAYMENT
          );
        case STATUS_BY_OPTIONS.PENDING_PAYMENT:
            return(
              app.applicationStatus === CONST.APPLICATION_STATUS_PENDING_PAYMENT
            )
        case STATUS_BY_OPTIONS.SUBMITTED:
          return (
            app.applicationStatus === CONST.APPLICATION_STATUS_UNREAD ||
            app.applicationStatus === CONST.APPLICATION_STATUS_UNDER_REVIEW ||
            app.applicationStatus === CONST.APPLICATION_STATUS_OPENED ||
            app.applicationStatus === CONST.APPLICATION_STATUS_PENDING_AGENCY
          );
        case STATUS_BY_OPTIONS.PENDING:
          return (
            app.applicationStatus === CONST.APPLICATION_STATUS_PENDING
          );
        case STATUS_BY_OPTIONS.APPROVED:
          return (
            app.applicationStatus === CONST.APPLICATION_STATUS_APPROVED_ISSUED &&
            app.licenceStatus === CONST.LICENCE_STATUS_ACTIVE
          );
        case STATUS_BY_OPTIONS.DENIED:
          return app.applicationStatus === CONST.APPLICATION_STATUS_DENIED;
        case STATUS_BY_OPTIONS.REVOKED:
          return (
            app.applicationStatus === CONST.APPLICATION_STATUS_APPROVED_ISSUED &&
            app.licenceStatus === CONST.LICENCE_STATUS_REVOKED
          );
        case STATUS_BY_OPTIONS.WITHDRAWN:
          return (
            app.applicationStatus === CONST.APPLICATION_STATUS_WITHDRAWN_BY_CLIENT
          );
        case STATUS_BY_OPTIONS.SUSPENDED:
          return (
            app.applicationStatus === CONST.APPLICATION_STATUS_APPROVED_ISSUED &&
            app.licenceStatus === CONST.LICENCE_STATUS_SUSPENDED
          );
        case STATUS_BY_OPTIONS.EXPIRED:
          return (
            app.applicationStatus === CONST.APPLICATION_STATUS_APPROVED_ISSUED &&
            app.licenceStatus === CONST.LICENCE_STATUS_EXPIRED
          );
        case STATUS_BY_OPTIONS.ALL:
          return true;
        default:
          // show all by default
          return true;
      }
    });
  }

  let sortedCollectors = [];

  if (components.welcome.agentSort === AGENT_SORT_TYPES.LAST_NAME) {
    sortedCollectors = filteredAdditionalApplications.slice().sort((p1, p2) => {
      let cmpVal = 0;

      // pop removes last element from array and returns the element
      const ln1 = p1.contactName?.trim().toLowerCase().split(" ").pop()
      const ln2 = p2.contactName?.trim().toLowerCase().split(" ").pop()

      if (ln1 > ln2) cmpVal = 1;
      else if (ln1 < ln2) cmpVal = -1;
      return cmpVal;
    });
  } else {
      if(isCollectionOrDebtAgencyLicence(agencyApplication?.licencingSubType)) {
        //Sort on full branch name
        sortedCollectors = filteredAdditionalApplications.slice().sort((a, b) => {
        const fn1 = a.contactName?.trim().toLowerCase();
        const fn2 = b.contactName?.trim().toLowerCase();

        if (!fn1 && !fn2) return 0;
        if (!fn1) return 1;
        if (!fn2) return -1;

        return fn1.localeCompare(fn2);
      });
    }
    else {
        sortedCollectors = filteredAdditionalApplications.slice().sort((p1, p2) => {
        let cmpVal = 0;
        // shift removes last element from array and returns the element
        const fn1 = p1.contactName?.trim().toLowerCase().split(" ").shift()
        const fn2 = p2.contactName?.trim().toLowerCase().split(" ").shift()

        if (fn1 > fn2) cmpVal = 1;
        else if (fn1 < fn2) cmpVal = -1;
        return cmpVal;
      });
    }
  }

  sortedCollectors.forEach((agentApp) => {
    // const agentAppBusinessLicenceData = agentApp.businessLicenceData

    if (
      (!agentApp.changeType || agentApp.changeType === "SAVE") &&
      !agentApp.markedDeleted
    ) {
      const licenceStatus = getLicenceStatus(agentApp);
      
      const licenceExpiry = agentApp.endDate ?? 0
      let isAgentRenewalEnable = (renewalStartDate && renewalEndDate) ? (
                            IsTodayWithinRange(renewalStartDate, renewalEndDate) && 
                            IsDateYearCurrentYear(licenceExpiry) && 
                            IsDateInFuture(licenceExpiry)) : false
      // case : licence has expired (post expiry date) but still in grace period
      const isExpiredInGracePeriod = (agentApp.licenceStatus === CONST.LICENCE_STATUS_EXPIRED) && IsDateInFuture(renewalEndDate)
      const isDraftInGracePeriod = (isRenewalTypeApplication(agentApp) 
                                        && (agentApp.applicationStatus === CONST.APPLICATION_STATUS_PENDING_PAYMENT) 
                                        && IsDateInFuture(renewalEndDate))
      isAgentRenewalEnable = isAgentRenewalEnable || isExpiredInGracePeriod || isDraftInGracePeriod

      const contactName = () => {
        if (agentApp.contactName && agentApp.contactName.indexOf("null") === -1) {
          return agentApp.contactName
        }

        if(agentApp.businessLicenceData?.additionalBranches && 
          Array.isArray(agentApp.businessLicenceData?.additionalBranches)
          && agentApp.businessLicenceData?.additionalBranches.length === 1) {
          const branchDetails = agentApp.businessLicenceData?.additionalBranches[0];
          return branchDetails.branchName;
        }
      }

      agentApplicationsStatus.push({
        applicationId: agentApp.id,
        applicationStatus: agentApp.applicationStatus,
        agentName: contactName(),
        licencingSubType : agentApp.licencingSubType,
        licenceStatus: licenceStatus,
        licenceNumber: agentApp.licenceNumber ? agentApp.licenceNumber : "",
        endDate: agentApp.endDate
          ? humanReadableDate(agentApp.endDate)
          : (agencyApplication.endDate ? humanReadableDate(agencyApplication.endDate) : "" ),
        submittedDate: agentApp.submittedDate
          ? humanReadableDate(agentApp.submittedDate)
          : null,
        lastModified: agentApp.modifiedDateTime
          ? humanReadableDate(agentApp.modifiedDateTime)
          : null,
        startDate: agentApp.startDate
          ? humanReadableDate(agentApp.startDate)
          : null,
        markedDoNotRenew : agentApp.markedDoNotRenew,
        isAgentRenewalEnable,
        cepRipExpiryDate: agentApp?.cepRipExpiryDate
      });
    }
  });

  return agentApplicationsStatus;
}

const welcomeSelector = createSelector(
  [
    (state) => state.components,
    (state) => state.primaryAgencyApplication.application,
    (state) => state.primaryAgencyApplication.licenceTypeDetails,
    (state) => state.additionalApplications,
    (state) => state.archiveApplications?.applications ?? [],
    mraPrincipalBrokerSelector,
  ],
  (
    components, 
    agencyApplication, 
    licenceTypeDetails, 
    additionalApplications, 
    archiveApplications,
    {principalBrokerApplication}
  ) => {
    let archiveApplication = null;
    const renewalDetails = [];

    if(Array.isArray(archiveApplications) && archiveApplications.length > 0) {
      archiveApplication = archiveApplications?.[0]
    }
    
    if(isRenewalTypeApplication(agencyApplication)) {
      let statuStr =
            agencyApplication.licenceStatus === CONST.LICENCE_STATUS_NEW
              ? agencyApplication.applicationStatus
              : agencyApplication.licenceStatus;

      if (!statuStr) {
        statuStr = CONST.LICENCE_STATUS_NEW;
      }
      
      renewalDetails.push({
        id: "licenceStatus",
        title: "Application status",
        status: statuStr,
        text: agencyApplicationStatusString(agencyApplication),
      });
    }

    const licenceType = licenceTypeDetails?.businessLicenceStatusList?.[0]
    const startDate = licenceType?.renewalStartDate
    const endDate = licenceType?.renewalGraceDate ?? licenceType?.renewalEndDate
    const licenceExpiry = agencyApplication?.endDate ?? 0

    let isAgencyRenewalEnable = (startDate && endDate) ? (
          IsTodayWithinRange(startDate, endDate) && 
          IsDateYearCurrentYear(licenceExpiry) && 
          IsDateInFuture(licenceExpiry)) : false

    // case : licence has expired (post expiry date) but still in grace period
    const isExpiredInGracePeriod = ((agencyApplication?.licenceStatus === CONST.LICENCE_STATUS_EXPIRED) && IsDateInFuture(endDate))
    const isDraftInGracePeriod = (isRenewalTypeApplication(agencyApplication) 
                                    && (agencyApplication.applicationStatus === CONST.APPLICATION_STATUS_PENDING_PAYMENT) 
                                    && IsDateInFuture(endDate))
    isAgencyRenewalEnable = isAgencyRenewalEnable || isExpiredInGracePeriod || isDraftInGracePeriod
    
    const enableStartNewApplication = (agencyApplication?.licenceStatus === CONST.LICENCE_STATUS_EXPIRED || 
                                        agencyApplication?.applicationStatus === CONST.APPLICATION_STATUS_PENDING_PAYMENT) && 
                                          !IsDateInFuture(endDate)

    const businessName = () => {
      if(agencyApplication?.licencingSubType === CONST.LICENCE_SUB_TYPE_INDIVIDUAL_PRINCIPAL_BROKER_APPLICATION
        || agencyApplication?.licencingSubType === CONST.LICENCE_SUB_TYPE_INDIVIDUAL_COMPLIANCE_OFFICER_APPLICATION) {
          return fullName(agencyApplication?.businessLicenceData?.personData);
      }
      return agencyApplication?.name
    }

    return Object.assign({}, components.welcome, {
      agencyRenewalDetails : renewalDetails,
      agencyApplicationDetails: agencyStatusDetails(
        (isRenewalTypeApplication(agencyApplication) && archiveApplication) ? 
        archiveApplication : agencyApplication, principalBrokerApplication),
      agentApplications: agentApplicationStatusDetails(agencyApplication,additionalApplications.agentApplications, components,startDate,endDate),
      branchApplications: agentApplicationStatusDetails(agencyApplication,additionalApplications.additionalBranchApplications, components,startDate,endDate),
      businessName: businessName(),
      welcomeNote : welcomePaneNote(agencyApplication, isAgencyRenewalEnable, licenceType?.renewalEndDate, enableStartNewApplication),
      applicationStatus: agencyApplication?.applicationStatus,
      licenceStatus: agencyApplication?.licenceStatus,
      archiveApplication,
      agencyApplication: agencyApplication,
      id: agencyApplication?.id,
      isRenewalEnable : isAgencyRenewalEnable,
      enableStartNewApplication,
      licenceTypeDetails: licenceType
    });
  }
);

export const welcomeSelect = (state) => ({
  welcome: welcomeSelector(state),
});


export const currentArchiveAppsSelect = createSelector(
  (state) => state.archiveApplications?.applications ?? [],
  (state) => state.primaryAgencyApplication?.application,
  (archiveApps, currentApplication) => {
    if(archiveApps && archiveApps.length) {
      for(const app of archiveApps) {
        if(currentApplication.id === app.originalApplicationId) {
          return app;
        }
      }
    }

    return null;
  }
);